import { LoadingOverlayComponent } from '@/app/atoms/loading-overlay/loading-overlay.component';
import { AgencyDto } from '@/app/common/general-types/agency.dto';
import { AgencyService } from '@/app/services/agency/agency.service';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormField, form, minLength, required } from '@angular/forms/signals';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'agency-ins-profile',
  imports: [FormField, LoadingOverlayComponent],
  templateUrl: './profile.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Profile {
  readonly agency = input<AgencyDto | undefined>(undefined);
  readonly agencyUpdated = output<AgencyDto>();

  agencyInformationModel = signal({
    agencyName: '',
    agencyAbout: '',
    agencyWebsite: '',
  });

  agencyInformationForm = form(this.agencyInformationModel, (schemaPath) => {
    required(schemaPath.agencyName, { message: 'Agency name is required' });
    minLength(schemaPath.agencyName, 2, { message: 'Agency name must be at least 2 characters long' });
    required(schemaPath.agencyWebsite, { message: 'Agency website is required' });
  });

  agencyInformationUpdated = signal(false);
  updatingAgencyInformation = signal(false);
  private isInitialized = false;
  private lastAgencyId: string | undefined;

  private agencyService = inject(AgencyService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const agency = this.agency();
      if (agency?.id === this.lastAgencyId) {
        return;
      }
      this.lastAgencyId = agency?.id;
      this.isInitialized = false;
      this.agencyInformationUpdated.set(false);
      this.agencyInformationModel.set({
        agencyName: agency?.name ?? '',
        agencyAbout: agency?.about ?? '',
        agencyWebsite: agency?.website ?? '',
      });
      this.isInitialized = true;
    });

    effect(() => {
      this.agencyInformationModel();
      if (this.isInitialized) {
        this.agencyInformationUpdated.set(true);
      }
    });
  }

  saveChanges(): void {
    const agency = this.agency();
    if (!agency?.id) {
      return;
    }

    const touched =
      this.agencyInformationForm.agencyName().touched() ||
      this.agencyInformationForm.agencyWebsite().touched() ||
      this.agencyInformationForm.agencyAbout().touched();

    if (!touched || !this.agencyInformationUpdated()) {
      return;
    }

    this.updatingAgencyInformation.set(true);
    this.agencyService
      .updateAgencyAccountPartial(agency.id, {
        name: this.agencyInformationModel().agencyName,
        about: this.agencyInformationModel().agencyAbout,
        website: this.agencyInformationModel().agencyWebsite,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.agencyUpdated.emit(updated);
          this.updatingAgencyInformation.set(false);
        },
        error: () => {
          this.updatingAgencyInformation.set(false);
        },
      });
  }
}
